import React, { FC, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertVariantsType } from '@airwallex/alert';
import { useGetInvoiceCommonData } from '@/hooks/useGetInvoice';
import { formatDate, DATE_PRECISION, DATE_STYLE } from '@/utils/formatDate';
import { BannerType } from '../Layout';
import { StoreContext } from '@/providers/storeProvider';
import i18n from '@/utils/i18n';
import { formatCardLast4, formatPaymentSource, formatTransaction } from '@/utils/formatPayment';

type BannerProps = {
  bannerType: BannerType;
};

const Banner: FC<BannerProps> = ({ bannerType }) => {
  const { t } = useTranslation();
  const { invoiceData } = useContext(StoreContext);
  const { dueAt, paidAt, lastPaymentAttemptAt, nextPaymentAttemptAt, pastPaymentAttemptCount, paidOutOfBand } =
    invoiceData.invoice;
  const { isSubscription, autoCharge, isInDunning, isDunningFailed } = useGetInvoiceCommonData(invoiceData.invoice);

  const getPaidAlertProps = useCallback(() => {
    return {
      variant: 'SUCCESS' as AlertVariantsType,
      heading:
        isSubscription && autoCharge ? t('invoice.alert.paid.auto_charge.header') : t('invoice.alert.paid.header'),
      children: paidOutOfBand
        ? t('invoice.alert.paid.not_use_pa.description')
        : invoiceData.transaction?.paymentMethodType
        ? t('invoice.alert.paid.use_pa.description', {
            paidAt: formatDate(paidAt, i18n.language, DATE_STYLE.NATURAL, DATE_PRECISION.DATE_ONLY),
            paymentMethod: formatTransaction(invoiceData.transaction),
            detail: formatCardLast4(invoiceData.transaction?.cardLast4),
          })
        : t('invoice.alert.paid.use_pa.no_method.description', {
            paidAt: formatDate(paidAt, i18n.language, DATE_STYLE.NATURAL, DATE_PRECISION.DATE_ONLY),
          }),
      // btnText: t('invoice.alert.paid.btnText'),
      // btnAction: () => {
      //   console.log('download receipt');
      // },
    };
  }, [isSubscription, paidAt, autoCharge, paidOutOfBand, invoiceData.transaction, t]);

  const getOverdueAlertProps = useCallback(() => {
    if (isSubscription && ((autoCharge && isInDunning) || isDunningFailed)) {
      return {
        variant: 'CRITICAL' as AlertVariantsType,
        heading: t('invoice.alert.retry.header'),
        children: isInDunning
          ? t('invoice.alert.retry.times.description', {
              paymentMethod: formatPaymentSource(invoiceData.paymentSource),
              detail: formatCardLast4(invoiceData.paymentSource?.cardLast4),
              lastPaymentAttemptAt: formatDate(
                lastPaymentAttemptAt,
                i18n.language,
                DATE_STYLE.NATURAL,
                DATE_PRECISION.DATE_ONLY
              ),
              nextPaymentAttemptAt: formatDate(
                nextPaymentAttemptAt,
                i18n.language,
                DATE_STYLE.NATURAL,
                DATE_PRECISION.DATE_ONLY
              ),
            })
          : t('invoice.alert.retry.all.description', {
              paymentMethod: formatPaymentSource(invoiceData.paymentSource),
              detail: formatCardLast4(invoiceData.paymentSource?.cardLast4),
              pastPaymentAttemptCount,
            }),
      };
    }
    return {
      variant: 'WARNING' as AlertVariantsType,
      heading: t('invoice.alert.overdue.header'),
      children: t('invoice.alert.overdue.description'),
    };
  }, [
    isSubscription,
    pastPaymentAttemptCount,
    lastPaymentAttemptAt,
    nextPaymentAttemptAt,
    autoCharge,
    isInDunning,
    isDunningFailed,
    invoiceData.paymentSource,
    t,
  ]);

  const alertProps = useMemo(() => {
    switch (bannerType) {
      case BannerType.VOID:
        return {
          variant: 'WARNING' as AlertVariantsType,
          heading: t('invoice.alert.void.header'),
          children: t('invoice.alert.void.description'),
        };
      case BannerType.PAID:
        return getPaidAlertProps();
      case BannerType.SCHEDULED:
        return {
          variant: 'INFORMATIONAL' as AlertVariantsType,
          heading: t('invoice.alert.scheduled.header'),
          children: t('invoice.alert.scheduled.description', {
            dueAt: formatDate(dueAt, i18n.language, DATE_STYLE.NATURAL, DATE_PRECISION.DATE_ONLY),
            paymentMethod: formatPaymentSource(invoiceData.paymentSource),
            detail: formatCardLast4(invoiceData.paymentSource?.cardLast4),
          }),
        };
      case BannerType.OVERDUE:
        return getOverdueAlertProps();
    }
    return null;
  }, [dueAt, bannerType, invoiceData.paymentSource, t, getPaidAlertProps, getOverdueAlertProps]);

  const { children, ...restProps } = alertProps || {};

  return (
    alertProps && (
      <Alert style={{ width: '100%' }} {...restProps}>
        {children}
      </Alert>
    )
  );
};

export default Banner;
